import React, { useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { Money } from '~/common';
import toPercentage from '~/common/masked/toPercentage';
import { Add, CaretDown } from '~/ui/assets/icons';
import { LOAN_TYPE_NAMES } from '~/typings/enums/LoanType';
import { OPERATION_STEPS_TYPES } from '~/typings/enums/OperationStepType';
import ContractStatus from '~/typings/enums/ContractStatus';
import { Shimmer } from '~/ui/components';
import {
  BackButton,
  CardWrapper,
  ContractDropdownTrigger,
  ContractNumber,
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  FieldValue,
  Header,
  HeaderLeft,
  MoreDetailsLink,
  MoreDetailsRow,
  OperationStepValue,
  ProductTag,
} from './CardContractSummaryCard.styled';

export type CardContractSummaryCardProps = {
  contractId: string;
  contractIds: string[];
  contractAuxIds?: Record<string, number>;
  loading: boolean;
  contractDetails: any;
  onBack?: () => void;
  onContractChange: (id: string) => void;
  onMoreDetails?: () => void;
};

export function CardContractSummaryCard({
  contractId,
  contractIds,
  contractAuxIds,
  loading,
  contractDetails,
  onBack,
  onContractChange,
  onMoreDetails,
}: CardContractSummaryCardProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const operationStep = contractDetails?.operationStep
    ? OPERATION_STEPS_TYPES[contractDetails.operationStep] || contractDetails.operationStep
    : '-';
  const product = contractDetails?.loanType
    ? LOAN_TYPE_NAMES[contractDetails.loanType] || contractDetails.loanType
    : null;
  const status = contractDetails?.contractInfo?.status
    ? ContractStatus(contractDetails.contractInfo.status) || contractDetails.contractInfo.status
    : '-';
  const contractNumber = contractDetails?.contractInfo?.id || '-';
  const availableLimit =
    contractDetails?.contractInfo?.availableLimit != null ? Money(contractDetails.contractInfo.availableLimit) : '-';
  const nominalRate =
    contractDetails?.contractInfo?.nominalRatePerMonth != null
      ? toPercentage(contractDetails.contractInfo.nominalRatePerMonth)
      : '-';

  return (
    <CardWrapper>
      <Header>
        <HeaderLeft>
          <ContractNumber>
            Contrato nº <strong>{contractId}</strong>
          </ContractNumber>
          {contractIds.length > 1 && (
            <Dropdown
              trigger={['click']}
              onVisibleChange={setDropdownOpen}
              overlay={
                <Menu selectedKeys={[contractId]} onClick={({ key }) => onContractChange(key as string)}>
                  {contractIds.map((id) => (
                    <Menu.Item key={id}>{contractAuxIds?.[id] ? `${id} (${contractAuxIds[id]})` : id}</Menu.Item>
                  ))}
                </Menu>
              }
            >
              <ContractDropdownTrigger type="button" title="Trocar contrato" $isOpen={dropdownOpen}>
                <CaretDown />
              </ContractDropdownTrigger>
            </Dropdown>
          )}
        </HeaderLeft>
        {onBack && (
          <BackButton type="button" onClick={onBack}>
            ← Voltar
          </BackButton>
        )}
      </Header>

      <FieldsGrid>
        <FieldGroup>
          <FieldLabel>Passo da esteira</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <OperationStepValue>{operationStep}</OperationStepValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Produto</FieldLabel>
          {loading && <Shimmer br={4} height={16} />}
          {!loading && product && <ProductTag>{product}</ProductTag>}
          {!loading && !product && <FieldValue>-</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Status do contrato</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{status}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>N. do contrato</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{contractNumber}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Limite disponível</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{availableLimit}</FieldValue>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Taxa nominal (mês)</FieldLabel>
          {loading ? <Shimmer br={4} height={16} /> : <FieldValue>{nominalRate}</FieldValue>}
        </FieldGroup>
      </FieldsGrid>

      {onMoreDetails && (
        <MoreDetailsRow>
          <MoreDetailsLink type="button" onClick={onMoreDetails}>
            <Add />
            Detalhes do contrato
          </MoreDetailsLink>
        </MoreDetailsRow>
      )}
    </CardWrapper>
  );
}
